import { FC, ReactNode } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useAddGiftsMutation } from '@telialt/core-eshop/src/hooks/mutations/useAddGiftsMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsGiftsSliderLoaderActive } from '@telialt/core-eshop/src/hooks/useIsGiftsSliderLoaderActive';
import { GiftLegalText } from '@telialt/core-eshop/src/product/LegalText';
import { Loader } from '@telialt/core-eshop/src/product/Loader';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { GiftRequirementType, ImageFormat, ImageType } from '@telialt/models/src/EshopModel';
import { selectGiftInCart } from '@telialt/utils/src/eshopSelectors';
import { getProductImageData } from '@telialt/utils/src/eshopUtils';
import { isEmpty } from '@telialt/utils/src/helpers';

import { useGiftDetailsModalControls } from '../GiftDetailsModal/useGiftDetailsModalControls';
import { RadioGiftGroup } from './components/RadioGiftGroup';
import { ReadOnlyGiftGroup } from './components/ReadOnlyGiftGroup';
import { GiftListHeading, GiftSelectorSection } from './GiftSelector.styles';
import { GiftOption } from './model';

export const GiftSelector: FC = () => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const { addGifts, addGiftsIsLoading } = useAddGiftsMutation();
    const isLoading = useIsGiftsSliderLoaderActive() || addGiftsIsLoading;
    const { openModal: openGiftDetailsModal } = useGiftDetailsModalControls();

    const potentialOfferPromotions = cart?.potentialOfferPromotions;

    if (isEmpty(potentialOfferPromotions)) {
        return null;
    }

    const benefits = potentialOfferPromotions[0].benefits;
    const giftRequirementType = potentialOfferPromotions[0]?.giftRequirementType;
    const legalText = cart?.promotionLegalText;

    const giftOptions: GiftOption[] = (benefits || [])
        .map((benefit): GiftOption => {
            const gift = benefit.gift;
            const imageData = getProductImageData(gift.images, ImageType.Primary, ImageFormat.Thumbnail);

            return (
                gift && {
                    code: gift.code,
                    name: gift.name,
                    price: gift.price?.formattedValue,
                    oldPriceCrossed:
                        benefit.showOldPriceCrossed &&
                        gift.standardPrice?.value !== gift.price?.value &&
                        gift.standardPrice?.formattedValue,
                    image: {
                        alt: imageData?.altText || gift.name,
                        src: imageData?.url,
                    },
                    onOpenDetails: () => openGiftDetailsModal([benefit, -1]),
                }
            );
        })
        .filter(Boolean);

    if (isEmpty(giftOptions)) {
        return null;
    }
    const selectedGift = selectGiftInCart(cart)?.code;

    let giftGroup: ReactNode;
    if (giftRequirementType === GiftRequirementType.Optional) {
        return null; // starting from [LTCONART-155], we don't support "OPTIONAL" giftRequirementType
    } else if (giftRequirementType === GiftRequirementType.AllMandatory || giftOptions.length === 1) {
        giftGroup = <ReadOnlyGiftGroup options={giftOptions} labelledBy="gift-selector-heading" />;
    } else if (giftRequirementType === GiftRequirementType.OneMandatory) {
        giftGroup = (
            <RadioGiftGroup
                defaultValue={selectedGift}
                onValueChange={(code) => addGifts([code])}
                options={giftOptions}
                labelledBy="gift-selector-heading"
            />
        );
    }

    return (
        <GiftSelectorSection data-test={ESHOP_TEST_ID.GIFT_LIST_SECTION}>
            <Loader isLoading={isLoading} dataTest={ESHOP_TEST_ID.GIFT_LIST_LOADER} />
            <GiftListHeading tag="h3" variant="title-100" id="gift-selector-heading">
                {t['eshop.product_page.gift_slider.heading']}
            </GiftListHeading>
            {giftGroup}
            {!!legalText && <GiftLegalText dataTest={ESHOP_TEST_ID.GIFT_LEGAL_TEXT} legalText={legalText} />}
        </GiftSelectorSection>
    );
};
